import _ from "lodash";
import {useEffect} from "react";
import {useParams} from "react-router-dom";
import {Alert, Grid, Paper, Stack, Typography} from "@mui/material";
import InvitationIcon from "@mui/icons-material/MailOutline";
import {AppLayout} from "../AppLayout";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {acceptUserInvitation, getInvitationById, rejectUserInvitation} from "../../services/invitationService";
import {swallowException} from "../../utils/appUtils";
import {
    getResourceErrorMessage,
    isResourceFailed,
    isResourceLoading,
    isResourceSucceeded,
} from "../../utils/resourceUtils";
import {ActionButton} from "../base/ActionButton";
import {KeyValuePair} from "../base/KeyValuePair";
import {useTranslation} from "react-i18next";
import {UserInvitationEx, UserInvitationStatus} from "../../types/domain/userInvitation";

export const UserInvitationPage: React.FC = () => {
    const {t} = useTranslation();
    const {invitationId} = useParams();
    const invitationRetrieval = useResourceManagement({getInvitationById});
    const invitationManagement = useResourceManagement({acceptUserInvitation, rejectUserInvitation});
    const {getInvitationById: getInvitation, resource: invitationState} = invitationRetrieval;
    const invitation = isResourceSucceeded(invitationState) ? invitationState.data : undefined;

    useEffect(() => {
        if (invitationId) {
            getInvitation(invitationId).catch(swallowException);
        }
    }, [getInvitation, invitationId]);

    const onAccept = () => {
        if (invitation) {
            invitationManagement.reset();
            invitationManagement
                .acceptUserInvitation(invitation.id)
                .then(() => getInvitation(invitation.id))
                .catch(swallowException);
        }
    };

    const onReject = () => {
        if (invitation) {
            invitationManagement.reset();
            invitationManagement
                .rejectUserInvitation(invitation.id)
                .then(() => getInvitation(invitation.id))
                .catch(swallowException);
        }
    };

    const getInvitationStatusText = (invitation: UserInvitationEx) => {
        if (!invitation) return "";

        const statusKey =
            invitation.status === UserInvitationStatus.ACCEPTED
                ? "accepted"
                : invitation.status === UserInvitationStatus.REJECTED
                  ? "rejected"
                  : "pending";

        return t(`notifications.invitation.status.${statusKey}`, {
            companyName: invitation.companyName,
        });
    };

    return (
        <AppLayout>
            <Stack
                direction="column"
                alignItems="center"
                paddingX="3rem"
                paddingTop="3rem"
                paddingBottom="0.5rem"
                gap="2.25rem"
            >
                <Stack
                    component={Paper}
                    elevation={0}
                    square
                    sx={{
                        minWidth: "50rem",
                        width: "50rem",
                    }}
                >
                    <Stack
                        direction="row"
                        gap="0.7rem"
                        paddingX="1.5rem"
                        paddingY="1.125rem"
                        sx={{
                            bgcolor: "#F3F5FA",
                        }}
                    >
                        <InvitationIcon color="primary" />
                        <Typography fontSize={16} textTransform="uppercase" data-test="header">
                            {t("user_invitation_page.title")}
                        </Typography>
                    </Stack>
                    <Stack direction="column">
                        {isResourceFailed(invitationManagement.resource) && (
                            <Alert severity="error" sx={{paddingX: "2.5rem"}}>
                                {getResourceErrorMessage(invitationManagement.resource)}
                            </Alert>
                        )}
                        <Stack
                            direction="column"
                            justifyContent="center"
                            sx={{
                                paddingY: "1.5rem",
                                paddingX: "1.5rem",
                            }}
                        >
                            {invitation && (
                                <Stack direction="column" gap="1.5rem">
                                    <Typography>{getInvitationStatusText(invitation)}</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <KeyValuePair header={t("notifications.invitation.company_name")}>
                                                <Typography>{invitation.companyName}</Typography>
                                            </KeyValuePair>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <KeyValuePair header="Role">
                                                <Typography>{_.capitalize(invitation.role)}</Typography>
                                            </KeyValuePair>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                    <Stack
                        direction="row"
                        justifyContent="end"
                        alignContent="right"
                        sx={{
                            bgcolor: "#F3F5FA",
                            paddingX: "1.5rem",
                            paddingY: "1.125rem",
                        }}
                    >
                        <Stack direction="row" gap="1rem">
                            <ActionButton
                                data-test="rejectInvitation"
                                variant="contained"
                                color="secondary"
                                icon="clear"
                                onClick={onReject}
                                disabled={
                                    isResourceLoading(invitationState) ||
                                    invitation?.status !== UserInvitationStatus.PENDING
                                }
                            >
                                {t("dialogs.actions.reject")}
                            </ActionButton>
                            <ActionButton
                                data-test="acceptInvitation"
                                variant="contained"
                                color="primary"
                                icon="check"
                                onClick={onAccept}
                                disabled={
                                    isResourceLoading(invitationState) ||
                                    invitation?.status !== UserInvitationStatus.PENDING
                                }
                            >
                                {t("dialogs.actions.accept")}
                            </ActionButton>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </AppLayout>
    );
};
